// src/pages/dashboard/branch/menu-items/components/tabs/ImageSettingsTab.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiUpload, FiX, FiImage, FiSettings } from 'react-icons/fi';
import Toggle from '../../../../../../../components/ui/Toggle';
import { FormData } from '../updateMenuItemModal';

interface ImageSettingsTabProps {
	formData: FormData;
	imageFile: File | null;
	imagePreview: string | null;
	onFormDataChange: (data: FormData) => void;
	onImageChange: (file: File | null) => void;
	onImagePreviewChange: (preview: string | null) => void;
}

const ImageSettingsTab: React.FC<ImageSettingsTabProps> = ({
	formData,
	imageFile,
	imagePreview,
	onFormDataChange,
	onImageChange,
	onImagePreviewChange
}) => {
	const { t } = useTranslation();

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			onImageChange(file);
			const reader = new FileReader();
			reader.onloadend = () => {
				onImagePreviewChange(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const removeImage = () => {
		onImageChange(null);
		onImagePreviewChange(null);
	};

	const handleToggleChange = (field: keyof FormData) => {
		onFormDataChange({
			...formData,
			[field]: !formData[field]
		});
	};
	const handleDivClick = () => {
		document.getElementById('image-upload')?.click();
	};
	return (
		<div className="space-y-6">
			{/* Image Upload Section */}
			<div className="space-y-4">
				<div className="flex items-center gap-2 mb-4">
					<FiImage className="w-5 h-5 text-primary" />
					<h4 className="text-md font-medium text-gray-900 dark:text-white">
						{t('menuItems.image')}
					</h4>
				</div>

				{!imagePreview ? (
					<div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 cursor-pointer" onClick={handleDivClick}>
						<div className="text-center">
							<FiUpload className="mx-auto h-12 w-12 text-gray-400" />
							<div className="mt-4">
								<label htmlFor="image-upload">
									<span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">
										{t('menuItems.uploadImage')}
									</span>
								</label>
							</div>
							<p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
								PNG, JPG, WebP up to 5MB
							</p>
						</div>
						<input
							id="image-upload"
							type="file"
							className="sr-only"
							accept="image/*"
							onChange={handleImageChange}
						/>
					</div>
				) : (
					<div className="relative">
						<img
							src={imagePreview}
							alt="Preview"
							className="w-full h-48 object-cover rounded-lg"
						/>
						<button
							type="button"
							onClick={removeImage}
							className="absolute top-2 end-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors cursor-pointer"
						>
							<FiX size={16} />
						</button>
					</div>
				)}
			</div>

			{/* Item Settings Section */}
			<div className="space-y-4">
				<div className="flex items-center gap-2 mb-4">
					<FiSettings className="w-5 h-5 text-primary" />
					<h4 className="text-md font-medium text-gray-900 dark:text-white">
						{t('menuItems.itemSettings')}
					</h4>
				</div>

				<div className="space-y-4">
					{/* Published Toggle */}
					<div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
						<div>
							<h5 className="text-sm font-medium text-gray-900 dark:text-white">
								{t('menuItems.published')}
							</h5>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								{formData.isPublished
									? t('menuItems.publishedHelper')
									: t('menuItems.draftHelper')
								}
							</p>
						</div>
						<Toggle
							enabled={formData.isPublished}
							onChange={() => handleToggleChange('isPublished')}
							activeColor='bg-green-500'
						/>
					</div>

					{/* Available Toggle */}
					<div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
						<div>
							<h5 className="text-sm font-medium text-gray-900 dark:text-white">
								{t('menuItems.available')}
							</h5>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								{formData.isAvailable
									? t('menuItems.availableHelper')
									: t('menuItems.unavailableHelper')
								}
							</p>
						</div>
						<Toggle
							enabled={formData.isAvailable}
							onChange={() => handleToggleChange('isAvailable')}
							activeColor='bg-blue-500'
						/>
					</div>

					{/* Featured Toggle */}
					<div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
						<div>
							<h5 className="text-sm font-medium text-gray-900 dark:text-white">
								{t('menuItems.featured')}
							</h5>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								{formData.isFeatured
									? t('menuItems.featuredHelper')
									: t('menuItems.notFeaturedHelper')
								}
							</p>
						</div>
						<Toggle
							enabled={formData.isFeatured}
							onChange={() => handleToggleChange('isFeatured')}
							activeColor='bg-orange-500'
						/>
					</div>
				</div>
			</div>

			{/* Item Preview */}
			{imagePreview && (
				<div className="space-y-4">
					<h4 className="text-md font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
						{t('menuItems.itemPreview')}
					</h4>

					<div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
						<div className="flex gap-4">
							<img
								src={imagePreview}
								alt="Item preview"
								className="w-20 h-20 object-cover rounded-lg"
							/>
							<div className="flex-1">
								<h5 className="font-medium text-gray-900 dark:text-white">
									{formData.name || t('menuItems.itemName')}
								</h5>
								<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
									{formData.description || t('menuItems.itemDescription')}
								</p>
								<div className="flex items-center gap-2 mt-2">
									<span className="font-semibold text-primary">
										${formData.price || '0.00'}
									</span>
									{formData.discountedPrice && (
										<span className="text-sm text-gray-500 line-through">
											${formData.discountedPrice}
										</span>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ImageSettingsTab;